import { styled } from "~/application/theme";
import { Card } from "~/components/Card";
import { EmptyState } from "~/components/EmptyState";
import { Flex } from "~/components/Flex";
import { Icon } from "~/components/Icon";
import {
  SvgAirplane,
  SvgArrowDown,
  SvgArrowUp,
  SvgBus,
  SvgCar,
  SvgHotels,
} from "~/components/Icon/icons";
import { Skeleton } from "~/components/Skeleton";
import { Text } from "~/components/Text";
import { ByProductResponse, PolicyComplianceResponse, ProductType } from "../types";
import { asCurrency } from "../utils";

export interface PolicyByProductProps {
  policyCompliance?: PolicyComplianceResponse;
  byProduct?: ByProductResponse;
  isFetching: boolean;
}

const Grid = styled("div", {
  display: "grid",
  gap: "$4",
  gridTemplateColumns: "1fr",
  "@md": {
    gridTemplateColumns: "1fr 1fr",
  },
});

const PRODUCT_KEYS: ProductType[] = ["airway", "hotel", "road", "vehicle"];

const PRODUCT_LABELS: Record<ProductType, string> = {
  airway: "Aéreo",
  hotel: "Hotel",
  road: "Rodoviário",
  vehicle: "Carro",
};

const PRODUCT_ICONS: Record<ProductType, React.ElementType> = {
  airway: SvgAirplane,
  hotel: SvgHotels,
  road: SvgBus,
  vehicle: SvgCar,
};

interface ProductCardData {
  key: ProductType;
  label: string;
  icon: React.ElementType;
  value: number;
  insidePolicy: number;
  outsidePolicy: number;
}

const formatPercentage = (value: number) => `${Math.round(value)}%`;

const buildCards = (
  policyCompliance: PolicyComplianceResponse,
  byProduct: ByProductResponse,
): ProductCardData[] =>
  PRODUCT_KEYS.map((key) => {
    const value = byProduct.series.reduce((sum, item) => sum + (item[key] ?? 0), 0);

    const rows = policyCompliance.compliance.filter((item) => item.productType === key);
    const notViolated = rows.reduce((sum, item) => sum + (item.notViolatedCount ?? 0), 0);
    const total = rows.reduce((sum, item) => sum + (item.totalCount ?? 0), 0);
    const insidePolicy = total > 0 ? (notViolated / total) * 100 : 0;

    return {
      key,
      label: PRODUCT_LABELS[key],
      icon: PRODUCT_ICONS[key],
      value,
      insidePolicy,
      outsidePolicy: 100 - insidePolicy,
    };
  });

export const PolicyByProduct = ({
  policyCompliance,
  byProduct,
  isFetching,
}: PolicyByProductProps) => {
  if (isFetching || !policyCompliance || !byProduct) {
    return (
      <Grid>
        {PRODUCT_KEYS.map((key) => (
          <Card key={key} css={{ overflow: "hidden" }}>
            <Flex css={{ width: "100%", height: "100%" }}>
              <Flex
                justify="center"
                align="center"
                css={{ background: "$neutral-100", p: "$5", width: "56px" }}
              >
                <Skeleton variant="icon-md" />
              </Flex>
              <Flex
                gap="4"
                justify="between"
                direction="column"
                css={{ px: "$5", py: "$4", width: "100%", flex: "1" }}
              >
                <Flex direction="column" gap="5">
                  <Skeleton variant="text-4" css={{ width: "40%" }} />
                  <Skeleton variant="text-6" css={{ width: "60%", mt: "$1" }} />
                </Flex>
                <Flex
                  gap="4"
                  align="start"
                  direction="column"
                  justify="center"
                  css={{ width: "100%", height: "50px", mt: "$2" }}
                >
                  <Skeleton variant="text-2" css={{ width: "80%" }} />
                  <Skeleton variant="text-2" css={{ width: "80%" }} />
                </Flex>
              </Flex>
            </Flex>
          </Card>
        ))}
      </Grid>
    );
  }

  if (policyCompliance.compliance.length === 0 && byProduct.series.length === 0) {
    return (
      <EmptyState>Nenhum dado de conformidade por produto disponível para o período.</EmptyState>
    );
  }

  const cards = buildCards(policyCompliance, byProduct);

  return (
    <Grid>
      {cards.map((card) => (
        <Card key={card.key} css={{ overflow: "hidden" }}>
          <Flex css={{ width: "100%", height: "100%" }}>
            <Flex
              justify="center"
              align="center"
              css={{ background: "$primary-base", p: "$5", height: "auto" }}
            >
              <Icon fill="#fff" as={card.icon} />
            </Flex>
            <Flex
              gap="4"
              justify="between"
              direction="column"
              css={{ px: "$5", py: "$4", width: "100%", flex: "1" }}
            >
              <Flex direction="column" gap="5">
                <Text css={{ fontWeight: "bold" }}>{card.label}</Text>
                <Text size="6" fw="700">
                  {asCurrency(card.value)}
                </Text>
              </Flex>
              <Flex
                gap="4"
                align="start"
                direction="column"
                justify="center"
                css={{ width: "100%", height: "50px" }}
              >
                <Flex gap="3" justify="center" align="center">
                  <Icon as={SvgArrowUp} css={{ fill: "$success-base" }} />
                  <Text>{formatPercentage(card.insidePolicy)} dentro da política</Text>
                </Flex>
                <Flex gap="3" justify="center" align="center">
                  <Icon as={SvgArrowDown} css={{ fill: "$error-base" }} />
                  <Text>{formatPercentage(card.outsidePolicy)} fora da política</Text>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      ))}
    </Grid>
  );
};

PolicyByProduct.displayName = "PolicyByProduct";
